import React, { useCallback } from "react";

import { useHistory, useParams } from "react-router-dom";

import usePatchTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePatchTransitionProposal";
import type {
  TransitionProposalId,
  TransitionProposalsImportTypeKey,
} from "@fartherfinance/frontend/api/TransitionProposals/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { strings } from "../../strings";
import { proposalCardButtons } from "@src/multiCustodian/components/TransitionProposals/constants";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import CardButton from "@src/sharedComponents/CardButton/CardButton";
import { actions } from "@src/sharedComponents/FlowContainer/reducer/actions";
import { useFlowContainerContext } from "@src/sharedComponents/FlowContainer/reducer/Context";
import GridRow from "@src/sharedComponents/GridRow/GridRow";
import Text from "@src/sharedComponents/Typography/Text";
import { handleApiError } from "@src/utils/handleApiError";

import styles from "./Start.module.css";

const GRID_AUTO_ROWS = "256px";

const Start = () => {
  const { clientId, transitionProposalId } = useParams<{
    clientId: ClientId;
    transitionProposalId: TransitionProposalId;
  }>();

  const history = useHistory();

  const notify = useStatusNotification();

  const { dispatch } = useFlowContainerContext();

  const auth = useRequestAuth();

  const { mutateAsync: updateTransitionProposal } =
    usePatchTransitionProposal(auth);

  const handleReplaceLocationUrl = useCallback(() => {
    history.replace(
      `/Client/${clientId}/Investments/TransitionProposals/${transitionProposalId}/Statements`
    );
  }, [clientId, transitionProposalId, history]);

  const handleOnClickCardButton = useCallback(
    (importType: TransitionProposalsImportTypeKey) => async () => {
      const body = { clientId, importType };

      dispatch({ type: actions.INCREMENT_PAGE_INDEX });

      if (importType === "statement") {
        handleReplaceLocationUrl();
      }

      try {
        await updateTransitionProposal({
          transitionProposalId,
          body,
        });
      } catch (error) {
        handleApiError({
          error,
          errorMessage: strings.ERROR_CREATING_PROPOSAL,
          genericErrorMessage: strings.ERROR_CREATING_PROPOSAL_GENERIC,
          notify,
        });

        dispatch({ type: actions.DECREMENT_PAGE_INDEX });
      }
    },
    [
      clientId,
      dispatch,
      notify,
      transitionProposalId,
      updateTransitionProposal,
      handleReplaceLocationUrl,
    ]
  );

  return (
    <GridRow
      className={styles.gridRow}
      gridAutoRows={GRID_AUTO_ROWS}
      maxColumns={3}
    >
      {proposalCardButtons.map((b, idx) => (
        <CardButton
          key={idx}
          header={b.title}
          onClick={handleOnClickCardButton(b.type)}
        >
          <Text color="textSubtle" variant="body">
            {b.description}
          </Text>
        </CardButton>
      ))}
    </GridRow>
  );
};

export default Start;
